import React, { useEffect, useRef, useState } from 'react';
import { BanIcon, MicOffIcon, MicIcon, PauseIcon, PlayIcon, SendIcon, Trash2Icon, XIcon } from 'lucide-react';
import type { ChatMessage, Participant, RoomState, Session } from '../types/session';
interface ChatPanelProps {
  session: Session;
  room: RoomState;
  messages: ChatMessage[];
  participants: Participant[];
  me: Participant | null;
  onSend: (text: string) => Promise<void>;
  onDeleteMessage: (id: string) => Promise<void>;
  onWipeChat: () => Promise<void>;
  onSetChatEnabled: (enabled: boolean) => Promise<void>;
  onSetMuted: (uid: string, muted: boolean) => Promise<void>;
  onSetBlocked: (uid: string, blocked: boolean) => Promise<void>;
}
const time = (ms: number | null) => ms ? new Date(ms).toLocaleTimeString([], {
  hour: 'numeric',
  minute: '2-digit'
}) : 'sending…';
export function ChatPanel({
  session,
  room,
  messages,
  participants,
  me,
  onSend,
  onDeleteMessage,
  onWipeChat,
  onSetChatEnabled,
  onSetMuted,
  onSetBlocked
}: ChatPanelProps) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [confirmWipe, setConfirmWipe] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const isHost = session.role === 'admin';
  useEffect(() => {
    endRef.current?.scrollIntoView({
      block: 'nearest'
    });
  }, [messages.length]);
  const blocked = me?.blocked === true;
  const muted = me?.muted === true;
  const paused = !room.chatEnabled;
  const closed = room.closed;
  const canSend = closed ? false : isHost ? !sending : !blocked && !muted && !paused && !sending;
  const send = async () => {
    const body = text.trim();
    if (!body || !canSend) return;
    setSending(true);
    try {
      await onSend(body);
      setText('');
    } finally {
      setSending(false);
    }
  };
  const byUid = (uid: string) => participants.find(p => p.id === uid);
  return <section aria-label="Live chat" className="flex min-h-0 flex-1 flex-col" data-id="element-27" data-mp="1jhrbhiyewzkg">
      {isHost && <div className="flex flex-wrap items-center gap-3 border-b border-line pb-3 text-xs" data-id="element-28" data-mp="1i2osb4mhrqef">
          <button type="button" onClick={() => onSetChatEnabled(paused)} className="inline-flex items-center gap-1.5 font-semibold text-body transition-colors duration-150 ease-emphasis hover:text-gold focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold" data-id="element-29" data-mp="1d210whucp7jo">
            {paused ? <PlayIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-30" data-mp="1vta82kgnfk83" /> : <PauseIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-31" data-mp="1vvmqeyqbjwlo" />}
            {paused ? 'Resume chat' : 'Pause chat'}
          </button>
          {confirmWipe ? <span className="inline-flex items-center gap-2" data-id="element-32" data-mp="1n0qa7m86ljob">
              <button type="button" onClick={async () => {
          await onWipeChat();
          setConfirmWipe(false);
        }} className="font-semibold text-crimson hover:text-[#E0655E] focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold" data-id="element-33" data-mp="2jjwhog6axck7">
                Wipe everything?
              </button>
              <button type="button" onClick={() => setConfirmWipe(false)} aria-label="Cancel wipe" className="text-muted hover:text-white" data-id="element-34" data-mp="a9ez4ye2zveg">
                <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-35" data-mp="k847o0k383i1" />
              </button>
            </span> : <button type="button" onClick={() => setConfirmWipe(true)} disabled={messages.length === 0} className="inline-flex items-center gap-1.5 font-semibold text-body transition-colors duration-150 ease-emphasis hover:text-crimson focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold disabled:cursor-not-allowed disabled:text-muted/50" data-id="element-36" data-mp="p54eidorhaa7">
              <Trash2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-37" data-mp="uagacdh2dt2k" />
              Wipe chat
            </button>}
          <span className="ml-auto tabular-nums text-muted" data-id="element-38" data-mp="pbdglp6jb99w">
            {participants.filter(p => !p.blocked).length} joined
          </span>
        </div>}

      <div className="min-h-0 flex-1 overflow-y-auto py-3 pr-1" data-id="element-39" data-mp="z1eu5n19h9ak">
        {messages.length === 0 ? <p className="py-10 text-center text-sm text-muted" data-id="element-40" data-mp="bbubl590se1t">
            No messages yet. Say hello to the room.
          </p> : <ul className="space-y-3" data-id="element-41" data-mp="6f3ucelhbcet">
            {messages.map(m => {
          const person = byUid(m.uid);
          const mine = m.uid === session.uid;
          return <li key={m.id} className="group" data-id="element-42" data-mp="g9vsbjiz41a4">
                  <div className="flex items-baseline gap-2" data-id="element-43" data-mp="1wpgjob8u6tr0">
                    <span className={`truncate text-xs font-semibold ${mine ? 'text-gold' : 'text-white'}`} data-id="element-44" data-mp="1wrtaldwkoeg7">
                      {m.name}
                      {person?.muted && isHost ? ' · muted' : ''}
                    </span>
                    <span className="shrink-0 text-[10px] tabular-nums text-muted" data-id="element-45" data-mp="1q9gef98sc6e7">
                      {time(m.createdAt)}
                    </span>
                    {isHost && <span className="ml-auto flex shrink-0 items-center gap-1 opacity-0 transition-opacity duration-150 ease-emphasis focus-within:opacity-100 group-hover:opacity-100" data-id="element-46" data-mp="1ucv46rmj014u">
                        {person && <>
                            <button type="button" onClick={() => onSetMuted(person.id, !person.muted)} aria-label={person.muted ? `Unmute ${person.name}` : `Mute ${person.name}`} className="rounded p-1 text-muted hover:text-gold focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold" data-id="element-47" data-mp="297j6jwwmqjg8">
                              {person.muted ? <MicIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-48" data-mp="36nc21qrknlke" /> : <MicOffIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-49" data-mp="2wn32c2ydfi09" />}
                            </button>
                            <button type="button" onClick={() => onSetBlocked(person.id, !person.blocked)} aria-label={person.blocked ? `Unblock ${person.name}` : `Block ${person.name}`} className="rounded p-1 text-muted hover:text-crimson focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold" data-id="element-50" data-mp="2rvbkqhx70mm9">
                              <BanIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-51" data-mp="23rc1f91ukich" />
                            </button>
                          </>}
                        <button type="button" onClick={() => onDeleteMessage(m.id)} aria-label="Delete message" className="rounded p-1 text-muted hover:text-crimson focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold" data-id="element-52" data-mp="2dq1hcwa6nsq3">
                          <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-53" data-mp="1uryo25alr9mh" />
                        </button>
                      </span>}
                  </div>
                  <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-body" data-id="element-54" data-mp="xb06rhy8hiln">
                    {m.text}
                  </p>
                </li>;
        })}
          </ul>}
        <div ref={endRef} data-id="element-55" data-mp="2ch2ba35lmzft" />
      </div>

      <div className="border-t border-line pt-3" data-id="element-56" data-mp="2ci3z787prgey">
        {closed ? <p className="py-2 text-sm text-muted" data-id="element-57" data-mp="2qh3cxktouawc">
            This event is closed. The chat is read-only — earlier messages stay visible.
          </p> : blocked ? <p className="py-2 text-sm text-crimson" data-id="element-58" data-mp="2zh8kbuyvt9ai">
            The host has blocked you from this chat.
          </p> : <>
            <form onSubmit={e => {
          e.preventDefault();
          send();
        }} className="flex items-end gap-2" data-id="element-59" data-mp="2veve75g839d7">
              <label className="sr-only" htmlFor="chat-input" data-id="element-60" data-mp="34900rcobpymx">
                Message the room
              </label>
              <textarea id="chat-input" rows={2} value={text} maxLength={500} onChange={e => setText(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }} disabled={!canSend} placeholder={muted ? 'You have been muted by the host' : paused && !isHost ? 'The host paused the chat' : 'Message the room'} className="min-w-0 flex-1 resize-none rounded-lg border border-line bg-raised px-3 py-2.5 text-sm text-white placeholder:text-muted/70 focus:border-gold focus:outline-none disabled:opacity-50" data-id="element-61" data-mp="3j5r2hslgzmfp" />
              <button type="submit" disabled={!canSend || text.trim().length === 0} aria-label="Send message" className="mb-[2px] inline-flex shrink-0 items-center justify-center rounded-lg bg-gold p-3 text-ink transition-[background-color,transform] duration-150 ease-emphasis hover:bg-[#F0C154] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold active:scale-[0.97] disabled:cursor-not-allowed disabled:bg-line disabled:text-muted disabled:active:scale-100" data-id="element-62" data-mp="3a099j6d4acry">
                <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-63" data-mp="1gr0e0twp9rrg" />
              </button>
            </form>
            {(muted || paused && !isHost) && <p className="mt-2 text-[11px] text-muted" data-id="element-64" data-mp="29fpagfmt98wv">
                {muted ? 'You are muted — the host can unmute you.' : 'Chat is paused by the host.'}
              </p>}
          </>}
      </div>
    </section>;
}